import { useEffect, useState } from "react";
import { api, StudentError } from "../studentClient.js";
import FileRow from "../../components/FileRow.js";

interface SubFile { name: string; s3Key: string; sizeBytes?: number; }
interface Week { weekKey: string; files: SubFile[]; status: string; submission: SubFile[]; reviewed?: boolean; note?: string; }
interface Lab { weekKey: string; files: SubFile[]; }

const weekLabel = (k: string) => (k.startsWith("lab") ? `Lab ${k.slice(3)}` : `Week ${k.slice(2)}`);
const statusClass = (s: string) => s.toLowerCase();

export default function HomeworkPage() {
  const [weeks, setWeeks] = useState<Week[]>([]);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    api<{ weeks: Week[]; labs: Lab[] }>("/student/homework")
      .then((d) => { setWeeks(d.weeks); setLabs(d.labs); })
      .catch((e) => setError(e instanceof StudentError ? e.message : "Failed to load homework"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  async function download(key: string) {
    // Open the tab synchronously inside the tap. On iOS Safari, calling
    // window.open() AFTER an await loses the user-gesture context and the popup
    // blocker silently kills it ("click, nothing happens"). Redirect the blank
    // tab once we have the presigned URL; fall back to the same tab if the
    // browser blocked the open entirely.
    const win = window.open("", "_blank");
    try {
      const { url } = await api<{ url: string }>(`/student/homework/download-url?key=${encodeURIComponent(key)}`);
      if (win) win.location.href = url;
      else window.location.href = url;
    } catch (e) {
      if (win) win.close();
      setError(e instanceof StudentError ? e.message : "Download failed");
    }
  }

  async function upload(weekKey: string, file: globalThis.File) {
    setBusy(weekKey); setError("");
    try {
      const { uploadUrl, s3Key } = await api<{ uploadUrl: string; s3Key: string }>(`/student/homework/${weekKey}/upload-url`, {
        method: "POST", body: { filename: file.name, contentType: file.type || "application/octet-stream" },
      });
      const put = await fetch(uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } });
      if (!put.ok) throw new Error("upload failed");
      await api(`/student/homework/${weekKey}/submit`, { method: "POST", body: { s3Key, filename: file.name } });
      load();
    } catch (e) { setError(e instanceof StudentError ? e.message : "Upload failed"); }
    finally { setBusy(""); }
  }

  if (loading) return <p className="subhead">Loading...</p>;

  let i = 0;
  return (
    <>
      <div className="hw-head">
        <h1>Homework</h1>
        <p className="subhead" style={{ marginBottom: 0 }}>Download each week's materials, then upload your completed work.</p>
        <span className="hw-deadline">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
          Weekly deadline - Saturday 9:00 PM
        </span>
      </div>

      {error && <div className="login-err" style={{ marginBottom: 14 }}>{error}</div>}

      {weeks.length === 0 && labs.length === 0 && (
        <div className="hw-blank">
          <div className="hw-blank-mark">✎</div>
          <p>No homework released yet.<br /><span className="sub">Your first set unlocks Monday morning.</span></p>
        </div>
      )}

      {weeks.map((w) => (
        <section key={w.weekKey} className={`hw-week is-${statusClass(w.status)}`} style={{ animationDelay: `${i++ * 70}ms` }}>
          <div className="hw-top">
            <div className="hw-wk">{weekLabel(w.weekKey)}</div>
            <span className={`hw-pill ${statusClass(w.status)}`}>{w.status}</span>
          </div>

          <div className="hw-lbl">Materials</div>
          <div className="hw-files">
            {w.files.map((f) => <FileRow key={f.s3Key} name={f.name} sizeBytes={f.sizeBytes} onClick={() => download(f.s3Key)} />)}
          </div>

          <div className="hw-lbl">Your submission</div>
          {w.submission.length > 0
            ? <div className="hw-files">{w.submission.map((f) => <FileRow key={f.s3Key} name={f.name} sizeBytes={f.sizeBytes} onClick={() => download(f.s3Key)} />)}</div>
            : <p className="hw-empty-sub">Nothing uploaded yet.</p>}

          {w.reviewed && (
            <div className="hw-reviewed">
              <span className="hw-reviewed-tag">✓ Reviewed by your instructor</span>
              {w.note && <p className="hw-reviewed-note">Note from your instructor: {w.note}</p>}
            </div>
          )}

          <label className="hw-upload">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 16V4M7 9l5-5 5 5M5 20h14" /></svg>
            {busy === w.weekKey ? "Uploading…" : w.submission.length > 0 ? "Upload another file" : "Upload completed work"}
            <input type="file" hidden disabled={!!busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(w.weekKey, f); e.currentTarget.value = ""; }} />
          </label>
        </section>
      ))}

      {labs.map((l) => (
        <section key={l.weekKey} className="hw-week is-lab" style={{ animationDelay: `${i++ * 70}ms` }}>
          <div className="hw-top">
            <div className="hw-wk">{weekLabel(l.weekKey)} <span className="lab-tag">download only</span></div>
          </div>
          <div className="hw-lbl">Materials</div>
          <div className="hw-files">
            {l.files.map((f) => <FileRow key={f.s3Key} name={f.name} sizeBytes={f.sizeBytes} onClick={() => download(f.s3Key)} />)}
          </div>
        </section>
      ))}
    </>
  );
}
